import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../auth/AuthAllapot.tsx';
import { biztonsagosVissza } from '../auth/belepes.ts';
import { supabase } from '../lib/supabase.ts';
import { Betolto, Gomb, HibaUzenet, Mezo } from '../ui/elemek.tsx';
import BelepesKeret from './BelepesKeret.tsx';

interface Beallitas { factorId: string; qr: string; titok: string }

export default function Mfa() {
  const { allapot, frissites, kijelentkezes } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const vissza = biztonsagosVissza(params.get('vissza'));

  const [kilepesHiba, setKilepesHiba] = useState<unknown>(null);
  const [beallitas, setBeallitas] = useState<Beallitas | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [kod, setKod] = useState('');
  const [toltes, setToltes] = useState(false);
  const [kodHiba, setKodHiba] = useState<string | null>(null);
  const [betoltesHiba, setBetoltesHiba] = useState<unknown>(null);
  const inditva = useRef<string | null>(null);

  useEffect(() => {
    if (allapot !== 'mfa_beallitas' && allapot !== 'mfa_kod') return;
    if (inditva.current === allapot) return; // az enroll nem futhat párhuzamosan kétszer
    inditva.current = allapot;
    void (async () => {
      try {
        const { data: lista, error } = await supabase.auth.mfa.listFactors();
        if (error) throw error;
        if (allapot === 'mfa_kod') {
          const f = lista.totp[0]; // a `totp` lista csak az ellenőrzött tényezőket tartalmazza
          if (!f) throw new Error('Nincs ellenőrzött hitelesítő alkalmazás.');
          setFactorId(f.id);
          return;
        }
        // A félbehagyott (nem ellenőrzött) tényezőket töröljük, különben az új beállítás névütközést ad.
        for (const f of lista.all.filter((x) => x.factor_type === 'totp' && x.status === 'unverified')) {
          const { error: torlesHiba } = await supabase.auth.mfa.unenroll({ factorId: f.id });
          if (torlesHiba) throw torlesHiba;
        }
        const { data, error: enrollHiba } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Hitelesítő alkalmazás' });
        if (enrollHiba) throw enrollHiba;
        setFactorId(data.id);
        setBeallitas({ factorId: data.id, qr: data.totp.qr_code, titok: data.totp.secret });
      } catch (hiba) {
        inditva.current = null;
        setBetoltesHiba(hiba);
      }
    })();
  }, [allapot]);

  if (allapot === 'betolt') return <Betolto />;
  if (allapot === 'kijelentkezve') return <Navigate replace to="/belepes" />;
  if (allapot === 'kesz') return <Navigate replace to={vissza} />;

  async function ellenorzes(e: FormEvent) {
    e.preventDefault();
    if (!factorId) return;
    setToltes(true);
    setKodHiba(null);
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: kod.trim() });
    if (error) {
      setToltes(false);
      setKodHiba('Hibás kód. Nézd meg, hogy a telefonod órája pontos-e.');
      return;
    }
    await frissites();
    await queryClient.invalidateQueries();
    navigate(vissza, { replace: true });
  }

  const beallitasMod = allapot === 'mfa_beallitas';
  return (
    <BelepesKeret cim={beallitasMod ? 'Kétlépcsős azonosítás beállítása' : 'Hitelesítő kód'}>
      {beallitasMod && (
        <div className="grid gap-4">
          <p>Nyisd meg a Google Authenticatort (vagy más hitelesítő alkalmazást), olvasd be a kódot, és írd be a 6 jegyű számot.</p>
          {beallitas && (
            <>
              <img src={beallitas.qr} alt="QR-kód a hitelesítő alkalmazáshoz" className="mx-auto size-48 rounded-xl bg-szoveg p-2" />
              <p className="text-sm text-szoveg-halk">Ha nem tudod beolvasni, írd be kézzel ezt a titkot:</p>
              <code data-testid="totp-titok" className="break-all rounded-xl bg-hatter-melyen px-3 py-2 text-sm">{beallitas.titok}</code>
            </>
          )}
        </div>
      )}
      <HibaUzenet hiba={betoltesHiba} />
      {(beallitas || !beallitasMod) && factorId && (
        <form onSubmit={ellenorzes} className="grid gap-4">
          <Mezo label="Hitelesítő kód" name="kod" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6}
            required value={kod} onChange={(e) => setKod(e.target.value.replace(/\D/g, ''))} />
          <Gomb type="submit" toltes={toltes} disabled={kod.length !== 6}>Ellenőrzés</Gomb>
        </form>
      )}
      {!betoltesHiba && !factorId && <Betolto />}
      {kodHiba && <p role="alert" className="rounded-xl border border-hiba bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">{kodHiba}</p>}
      {!beallitasMod && <p className="text-sm text-szoveg-halk">Elvesztetted a telefonod? Szólj az irodavezetődnek, ő visszaállíthatja.</p>}
      <HibaUzenet hiba={kilepesHiba} />
      <Gomb valtozat="szoveg" onClick={() => { setKilepesHiba(null); kijelentkezes().catch(setKilepesHiba); }}>Kijelentkezés</Gomb>
    </BelepesKeret>
  );
}
